import React from 'react';
import { ActiveCustomer } from '../../../types/organizations';

interface GovernanceOverlay {
  id: string;
  name: string;
  description: string;
}

interface GovernanceOverlaysAssignmentModalProps {
  isOpen: boolean;
  customer: ActiveCustomer | null;
  availableGovernanceOverlays: GovernanceOverlay[];
  selectedGovernanceOverlays: string[];
  assignmentLoading: boolean;
  assignmentError: string;
  onClose: () => void;
  onToggleOverlay: (overlayId: string) => void;
  onSubmit: () => void;
}

const GovernanceOverlaysAssignmentModal: React.FC<GovernanceOverlaysAssignmentModalProps> = ({
  isOpen,
  customer,
  availableGovernanceOverlays,
  selectedGovernanceOverlays,
  assignmentLoading,
  assignmentError,
  onClose,
  onToggleOverlay,
  onSubmit,
}) => {
  if (!isOpen || !customer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Assign Governance Overlays - {customer.name}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* Available Governance Overlays */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Select Governance Overlays</h4>
            <div className="space-y-2 max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {availableGovernanceOverlays.length > 0 ? (
                availableGovernanceOverlays.map((overlay) => (
                  <label key={overlay.id} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedGovernanceOverlays.includes(overlay.id)}
                      onChange={() => onToggleOverlay(overlay.id)}
                      className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">{overlay.name}</div>
                      <div className="text-sm text-gray-600 mt-1">{overlay.description}</div>
                    </div>
                  </label>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  All available governance overlays are already connected to this customer.
                </div>
              )}
            </div>
          </div>

          {assignmentError && (
            <div className="text-red-600 text-sm">{assignmentError}</div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={assignmentLoading || selectedGovernanceOverlays.length === 0}
              className={`px-4 py-2 rounded-md transition-colors ${
                assignmentLoading || selectedGovernanceOverlays.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-orange-600 text-white hover:bg-orange-700'
              }`}
            >
              {assignmentLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Assigning...
                </div>
              ) : (
                `Assign ${selectedGovernanceOverlays.length} Overlay${selectedGovernanceOverlays.length !== 1 ? 's' : ''}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GovernanceOverlaysAssignmentModal;
